import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Heart,
  MessageCircle,
  Send,
  Image,
  Video,
  Link,
  MoreVertical,
  Pin,
  Trash2,
  Plus,
  Users,
  X,
  Play,
  Award
} from "lucide-react";
import { toast } from "sonner";

const Community = () => {
  const { user, isMemberOrAbove, isCoachOrAdmin } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostText, setNewPostText] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState(null); // "image", "video", or null
  const [mediaPreview, setMediaPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [userBadges, setUserBadges] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/community/posts`);
      setPosts(res.data);
      
      // Fetch badges for all users who posted
      const userIds = [...new Set(res.data.map(p => p.userId))];
      const badgesMap = {};
      for (const uid of userIds) {
        try {
          const badgesRes = await axios.get(`${API}/badges/user/${uid}`);
          if (badgesRes.data.length > 0) {
            badgesMap[uid] = badgesRes.data[0]; // Show first badge
          }
        } catch (e) {}
      }
      setUserBadges(badgesMap);
    } catch (err) {
      console.error("Posts fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (file.type.startsWith('image/')) {
      setMediaType('image');
    } else if (file.type.startsWith('video/')) {
      setMediaType('video');
    } else {
      toast.error("Please select an image or video file");
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setMediaPreview(e.target?.result);
    };
    reader.readAsDataURL(file);
    
    // Convert to base64 for storage (in a real app, upload to cloud storage)
    const base64Reader = new FileReader();
    base64Reader.onload = (e) => {
      setMediaUrl(e.target?.result);
    };
    base64Reader.readAsDataURL(file);
  };

  const handleUrlInput = (url) => {
    setMediaUrl(url);
    setMediaPreview(url);
    
    // Detect media type from URL
    if (url.match(/\.(jpg|jpeg|png|gif|webp)$/i) || url.includes('unsplash') || url.includes('pexels')) {
      setMediaType('image');
    } else if (url.match(/\.(mp4|webm|mov)$/i) || url.includes('youtube') || url.includes('vimeo')) {
      setMediaType('video');
    } else {
      setMediaType('image'); // Default to image
    }
  };

  const clearMedia = () => {
    setMediaUrl("");
    setMediaPreview(null);
    setMediaType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const createPost = async () => {
    if (!newPostText.trim() && !mediaUrl) {
      toast.error("Please write something or add media");
      return;
    }
    
    if (!isMemberOrAbove()) {
      toast.error("Available for TRIBE members");
      return;
    }
    
    setPosting(true);
    try {
      await axios.post(`${API}/community/posts`, {
        text: newPostText,
        imageUrl: mediaUrl || null,
        mediaType: mediaType
      });
      toast.success("Post shared with the whānau!");
      setNewPostText("");
      clearMedia();
      setShowNewPost(false);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  const likePost = async (postId) => {
    try {
      const res = await axios.post(`${API}/community/posts/${postId}/like`);
      setPosts(posts.map(p => 
        p.id === postId 
          ? { ...p, likesCount: res.data.likesCount, likedByUser: res.data.likedByUser }
          : p
      ));
    } catch (err) {
      toast.error("Failed to like post");
    }
  };

  const deletePost = async (postId) => {
    try {
      await axios.delete(`${API}/community/posts/${postId}`);
      toast.success("Post deleted");
      fetchPosts();
    } catch (err) {
      toast.error("Failed to delete post");
    }
  };

  const pinPost = async (postId) => {
    try {
      await axios.patch(`${API}/community/posts/${postId}/pin`);
      toast.success("Post pinned");
      fetchPosts();
    } catch (err) {
      toast.error("Failed to pin post");
    }
  };

  const fetchComments = async (postId) => {
    setLoadingComments(true);
    try {
      const res = await axios.get(`${API}/community/comments/${postId}`);
      setComments(res.data);
    } catch (err) {
      console.error("Comments fetch error:", err);
    } finally {
      setLoadingComments(false);
    }
  };

  const openComments = (post) => {
    setSelectedPost(post);
    fetchComments(post.id);
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API}/community/comments`, {
        postId: selectedPost.id,
        commentText: newComment
      });
      setNewComment("");
      fetchComments(selectedPost.id);
      fetchPosts();
    } catch (err) {
      toast.error("Failed to add comment");
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/community/comments/${commentId}`);
      fetchComments(selectedPost.id);
    } catch (err) {
      toast.error("Failed to delete comment");
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-8 max-w-lg mx-auto" data-testid="community">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-brand text-[#0B3D2E] flex items-center gap-2">
            <Users className="w-6 h-6" /> TRIBE
          </h1>
          <p className="text-sm text-[#4A5D56]">Our whānau community</p>
        </div>
        <Dialog open={showNewPost} onOpenChange={setShowNewPost}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-full" 
              data-testid="new-post-btn"
              disabled={!isMemberOrAbove()}
            >
              <Plus className="w-4 h-4 mr-1" /> Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md mx-4">
            <DialogHeader>
              <DialogTitle className="font-brand">Share with the whānau</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Textarea
                placeholder="What's on your mind?"
                value={newPostText}
                onChange={(e) => setNewPostText(e.target.value)}
                className="min-h-[100px] bg-[#F9F9F7] border-0 rounded-xl resize-none"
                data-testid="post-text-input"
              />
              
              {/* Media Preview */}
              {mediaPreview && (
                <div className="relative rounded-xl overflow-hidden">
                  {mediaType === 'video' ? (
                    <div className="relative bg-black/10 aspect-video flex items-center justify-center">
                      <video 
                        src={mediaPreview} 
                        className="max-h-48 max-w-full" 
                        controls 
                      />
                    </div>
                  ) : (
                    <img 
                      src={mediaPreview} 
                      alt="Preview" 
                      className="w-full max-h-48 object-cover"
                    />
                  )}
                  <button
                    onClick={clearMedia}
                    className="absolute top-2 right-2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {/* Media Upload Options */}
              {!mediaPreview && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*,video/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 h-12 border-[#0B3D2E]/20"
                    >
                      <Image className="w-4 h-4 mr-2" />
                      Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 h-12 border-[#0B3D2E]/20"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Video
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Link className="w-4 h-4 text-[#8FA39B]" />
                    <Input
                      placeholder="Or paste image/video URL..."
                      value={mediaUrl}
                      onChange={(e) => handleUrlInput(e.target.value)}
                      className="h-10 bg-[#F9F9F7] border-0 rounded-xl text-sm"
                    />
                  </div>
                </div>
              )}
              
              <Button 
                onClick={createPost}
                disabled={posting}
                className="w-full h-12 bg-[#0B3D2E] text-white rounded-full"
                data-testid="submit-post-btn"
              >
                {posting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <><Send className="w-4 h-4 mr-1" /> Share</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4">
        {posts.length === 0 ? (
          <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-[#0B3D2E]/30 mb-4" />
              <h3 className="text-lg font-semibold text-[#051F17] mb-2">
                Welcome to TRIBE
              </h3>
              <p className="text-sm text-[#4A5D56]">
                Be the first to share with the whānau
              </p>
            </CardContent>
          </Card>
        ) : (
          posts.map((post) => (
            <Card 
              key={post.id} 
              className={`bg-white border rounded-2xl overflow-hidden ${post.pinned ? 'border-[#D4A84B] ring-1 ring-[#D4A84B]' : 'border-[#0B3D2E]/5'}`}
              data-testid={`post-${post.id}`}
            >
              <CardContent className="p-4">
                {/* Post Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10 bg-[#0B3D2E]">
                      {post.userProfilePhoto ? (
                        <AvatarImage src={post.userProfilePhoto} />
                      ) : null}
                      <AvatarFallback className="bg-[#0B3D2E] text-white text-sm">
                        {getInitials(post.userName)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[#051F17]">{post.userName}</p>
                        {userBadges[post.userId] && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-[#D4A84B]/10 text-[#D4A84B] rounded-full flex items-center gap-0.5">
                            <Award className="w-2.5 h-2.5" />
                            {userBadges[post.userId].badgeName}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#8FA39B]">{formatTime(post.createdAt)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {post.pinned && (
                      <Pin className="w-4 h-4 text-[#D4A84B]" />
                    )}
                    {(post.userId === user.id || isCoachOrAdmin()) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {isCoachOrAdmin() && (
                            <DropdownMenuItem onClick={() => pinPost(post.id)}>
                              <Pin className="w-4 h-4 mr-2" />
                              {post.pinned ? "Unpin" : "Pin"} Post
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => deletePost(post.id)}
                            className="text-red-500"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {/* Post Content */}
                <p className="text-[#051F17] mb-3 whitespace-pre-wrap">{post.text}</p>
                
                {/* Media Display */}
                {post.imageUrl && (
                  post.mediaType === 'video' ? (
                    <div className="relative rounded-xl overflow-hidden bg-black/5 mb-3">
                      <video 
                        src={post.imageUrl} 
                        controls 
                        className="w-full max-h-80"
                        poster=""
                      />
                    </div>
                  ) : (
                    <img 
                      src={post.imageUrl} 
                      alt="Post"
                      className="w-full rounded-xl mb-3 object-cover max-h-80"
                    />
                  )
                )}

                {/* Post Actions */}
                <div className="flex items-center gap-4 pt-2 border-t border-[#0B3D2E]/10">
                  <button 
                    onClick={() => likePost(post.id)}
                    className={`flex items-center gap-1 text-sm transition-colors ${
                      post.likedByUser ? 'text-red-500' : 'text-[#4A5D56] hover:text-red-500'
                    }`}
                    data-testid={`like-btn-${post.id}`}
                  >
                    <Heart className={`w-5 h-5 ${post.likedByUser ? 'fill-current' : ''}`} />
                    <span>{post.likesCount}</span>
                  </button>
                  <button 
                    onClick={() => openComments(post)}
                    className="flex items-center gap-1 text-sm text-[#4A5D56] hover:text-[#0B3D2E] transition-colors"
                    data-testid={`comments-btn-${post.id}`}
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>Comment</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Member notice for free users */}
      {!isMemberOrAbove() && (
        <div className="mt-6 p-4 bg-[#E3EBE5]/30 rounded-xl text-center">
          <p className="text-sm text-[#4A5D56]">
            Posting is available for TRIBE members.
          </p>
        </div>
      )}

      {/* Comments Dialog */}
      <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <DialogContent className="max-w-md mx-4 max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-brand">Comments</DialogTitle>
          </DialogHeader>
          
          {selectedPost && (
            <div className="space-y-4">
              {/* Original Post Preview */}
              <div className="p-3 bg-[#F9F9F7] rounded-xl">
                <p className="font-medium text-sm text-[#051F17]">{selectedPost.userName}</p>
                <p className="text-sm text-[#4A5D56] line-clamp-2">{selectedPost.text}</p>
              </div>

              {/* Comments List */}
              <ScrollArea className="h-[300px]">
                {loadingComments ? (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
                  </div>
                ) : comments.length === 0 ? (
                  <p className="text-center text-sm text-[#8FA39B] py-8">
                    No comments yet. Be the first!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <div key={comment.id} className="flex gap-3">
                        <Avatar className="w-8 h-8 bg-[#0B3D2E] flex-shrink-0">
                          <AvatarFallback className="bg-[#0B3D2E] text-white text-xs">
                            {getInitials(comment.userName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 bg-[#F9F9F7] rounded-xl p-3">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-sm text-[#051F17]">{comment.userName}</p>
                            {(comment.userId === user.id || isCoachOrAdmin()) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-500"
                                onClick={() => deleteComment(comment.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                          <p className="text-sm text-[#4A5D56]">{comment.commentText}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Add Comment */}
              <div className="flex gap-2">
                <Input
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
                  onKeyPress={(e) => e.key === 'Enter' && addComment()}
                  data-testid="comment-input"
                />
                <Button 
                  onClick={addComment}
                  className="h-12 px-4 bg-[#0B3D2E] text-white rounded-xl"
                  data-testid="submit-comment-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Community;
